import type { PlatformMeta } from "@theta/api-client";

/**
 * HOW BIG AN UPLOAD MAY BE — the proxy ceiling, and the refusal before it
 *
 * THE BUG THIS FILE EXISTS FOR. `next.config.ts` proxies `/api/v1/*` to the
 * backend, and Next buffers a proxied request body through
 * `getCloneableBody()` (`next/dist/server/next-server.js` → `router-server.js`,
 * which calls `cloneBodyStream()` for every external rewrite). That buffer is
 * capped at 10 MB unless `experimental.proxyClientMaxBodySize` says otherwise,
 * and when it is exceeded the clone is simply CUT SHORT — the origin is left
 * waiting for bytes the declared `Content-Length` promised, the socket hangs
 * up, and the browser gets an opaque `HTTP 500` about thirty seconds later.
 * So every iPhone video — a minute of 1080p is 60–90 MB — failed, on a
 * platform whose own `GET /api/v1/meta` advertises `maxVideoSizeKb: 102400`.
 *
 * Raising the ceiling is the fix rather than routing uploads around the proxy:
 * the rewrite is what makes `/api/v1` SAME-ORIGIN for the browser, which is
 * what makes the session cookie first-party and CORS a non-problem (see the
 * rewrite table's header). An upload is the one request that most needs the
 * session, so it is the last one to take off the same origin.
 *
 * THE CEILING IS THE BACKEND'S NUMBER, NOT A SECOND OPINION. `next.config.ts`
 * is evaluated at BUILD time, so it cannot ask a running server what it
 * accepts; the value below mirrors the LARGEST of the three upload-class
 * defaults in `backend/src/config.ts`, which is the largest body the backend
 * will ever accept. `__tests__/upload-limits.test.ts` reads that file and
 * fails if this stops covering it, so the two cannot drift apart quietly.
 *
 * WHICH CLASS THAT IS DEPENDS ON THE DEPLOYMENT. Video and attachments are
 * both 100 MB since OSO-700 raised the attachment class, so the number is the
 * same either way — but with `VIDEO_HOST=bunny` the browser streams video
 * straight to Bunny over TUS and not one video byte passes through this proxy,
 * and what the ceiling carries there is a 100 MB PDF or recording.
 *
 * IT IS NOT RAISED FURTHER "TO BE SAFE". Every byte under it is a byte Next
 * buffers in memory, and the backend's own cap is what makes that bounded;
 * doubling the ceiling so that an oversized upload could reach the server and
 * be refused politely would trade a 4-core box's memory for a nicer message
 * in a case nothing should reach. Past the ceiling the truncation is as ugly
 * as it ever was — which is why `rejectionForFile` below exists, and why the
 * composer never sends a file it can already see is too big.
 *
 * A CLIENT still asks the server what the limits ARE: `GET /meta` is the
 * runtime authority, an operator may move any of the three by env, and the
 * endpoint exists precisely so a client does not compile them in.
 */

/** 100 MB — the backend's largest class default, video and attachments alike. */
export const MAX_UPLOAD_SIZE_KB = 102_400;

/**
 * What the proxy will pass, in bytes.
 *
 * A megabyte MORE than the largest file, because the proxy measures the
 * multipart ENVELOPE — the file plus its part headers, the `scope` field and
 * the boundaries — while the backend measures the file. A ceiling set to the
 * file limit exactly would truncate a file sitting AT the limit, which is
 * precisely the file the backend was about to accept.
 */
export const PROXY_MAX_BODY_BYTES = (MAX_UPLOAD_SIZE_KB + 1024) * 1024;

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;

/** Lower-case, no dot; `""` when the name has no extension. */
function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? "" : filename.slice(dot + 1).toLowerCase();
}

/**
 * Which of the server's three limits applies to this file, in KB.
 *
 * The classes mirror `backend/src/modules/uploads/hygiene.ts#prepareUpload`:
 * video its own class, images theirs, everything else the attachment limit.
 *
 * VIDEO IS MATCHED BY EXTENSION, NOT BY MIME TYPE, and that is the whole
 * reason this does not simply reuse `kindForFile`. A `.mov` picked in Safari
 * routinely arrives with an empty `File.type`, which `kindForFile` reads as
 * "file" — harmless when it only decides which chip to draw, and a false
 * refusal of a perfectly legal 24 MB video the moment it decides a limit.
 * `videoExtensions` comes off `/meta`, so it is the server's own list.
 */
export function limitKbForFile(
  file: File,
  limits: PlatformMeta["uploads"],
): number {
  switch (classOf(file, limits)) {
    case "video":
      return limits.maxVideoSizeKb;
    case "image":
      return limits.maxImageSizeKb;
    default:
      return limits.maxAttachmentSizeKb;
  }
}

type UploadClass = "image" | "video" | "file";

function classOf(file: File, limits: PlatformMeta["uploads"]): UploadClass {
  if (limits.videoExtensions.includes(extensionOf(file.name))) return "video";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("image/")) return "image";
  return "file";
}

function formatMb(bytes: number): string {
  return `${(bytes / BYTES_PER_MB).toLocaleString("da-DK", {
    maximumFractionDigits: 1,
  })} MB`;
}

const NOUNS: Record<UploadClass, string> = {
  image: "Billedet er for stort",
  video: "Videoen er for stor",
  file: "Filen er for stor",
};

/**
 * `null` when the file is acceptable, otherwise why it was refused — in the
 * words the server would have used.
 *
 * "…er for stor. Grænsen er N MB." is `prepareUpload`'s own sentence, so the
 * member reads the same thing whether the refusal happened here or there. The
 * file's own size is added because "for stor" without a number leaves them
 * guessing whether trimming a second off the clip would have done it.
 *
 * `limits` is `undefined` while `/meta` is still in flight or if it failed —
 * nothing is refused then, and the server stays the authority it always was.
 */
export function rejectionForFile(
  file: File,
  limits: PlatformMeta["uploads"] | undefined,
): string | null {
  if (!limits) return null;
  const limitKb = limitKbForFile(file, limits);
  if (file.size <= limitKb * BYTES_PER_KB) return null;
  return `${NOUNS[classOf(file, limits)]} (${formatMb(file.size)}). Grænsen er ${Math.floor(
    limitKb / BYTES_PER_KB,
  )} MB.`;
}
